import { getNewAuthToken } from '../../utils/common';
import config from '../../config';
import { MilestoneSubStatus, TicketStatus } from '../../pages/Ticket/TicketController';

export const isPavMilestone = (milestone) => (
    String(milestone?.type || '').trim().toUpperCase() === 'PAV'
);

export const getPavMilestoneLabel = (milestone) => (
    milestone?.type || milestone?.title
);

export const isPavMilestoneSignOffUnlocked = (milestone) => (
    isPavMilestone(milestone) && milestone?.is_signoff_locked !== true
);

const PAV_VERIFICATION_ROLES = ['SCM'];

// Physical verification of a PAV asset is available to SCM and customer users,
// both from the ticket actions menu and inside the asset verification screen.
export const canVerifyPavAssets = (newLoggedUserData, ticket_org_id) => (
    newLoggedUserData.org.id === ticket_org_id ||
    (Array.isArray(newLoggedUserData?.user?.roles) ? newLoggedUserData.user.roles : [])
        .some(({ label }) => PAV_VERIFICATION_ROLES.includes(String(label).toUpperCase()))
);

const getEntityId = (entity) => entity && typeof entity === 'object' ? entity.id : entity;
const getAssetMilestoneId = (asset) => (
    getEntityId(asset?.milestone_id)
    || getEntityId(asset?.ticket_milestone_id)
    || getEntityId(asset?.fk_ticket_milestone_id)
    || getEntityId(asset?.fk_milestone_id)
    || getEntityId(asset?.milestoneId)
    || getEntityId(asset?.ticketMilestoneId)
    || getEntityId(asset?.milestone)
    || getEntityId(asset?.ticket_milestone)
    || getEntityId(asset?.metadata?.milestone_id)
    || getEntityId(asset?.asset?.milestone_id)
    || getEntityId(asset?.asset?.ticket_milestone_id)
);

const getAssetIdentities = (asset) => [...new Set([
    getEntityId(asset?.asset?.id),
    getEntityId(asset?.asset_id),
    getEntityId(asset?.fk_asset_id),
    getEntityId(asset?.id),
    getEntityId(asset?.inventory_ledger_id),
].filter((identity) => identity !== undefined && identity !== null).map(String))];

export const getPavMilestoneAssets = (ticket, milestone) => {
    if (!isPavMilestone(milestone)) return [];

    const belongsToMilestone = (asset, allowMissingMilestoneId = false) => {
        const assetMilestoneId = getAssetMilestoneId(asset);
        if (assetMilestoneId === undefined || assetMilestoneId === null) return allowMissingMilestoneId;
        return String(assetMilestoneId) === String(milestone?.id);
    };
    const milestoneAssets = [
        ...(Array.isArray(milestone?.assets) ? milestone.assets : []),
        ...(Array.isArray(milestone?.inventory_ledger) ? milestone.inventory_ledger : []),
    ].filter((asset) => belongsToMilestone(asset, true));
    const ticketAssets = [
        ...(Array.isArray(ticket?.assets) ? ticket.assets : []),
        ...(Array.isArray(ticket?.inventory_ledger) ? ticket.inventory_ledger : []),
    ].filter((asset) => belongsToMilestone(asset));

    const seenIds = new Set();
    const seenObjects = new Set();
    return [...milestoneAssets, ...ticketAssets].filter((asset) => {
        if (!asset || typeof asset !== 'object') return false;
        const identities = getAssetIdentities(asset);
        if (identities.length > 0) {
            if (identities.some((identity) => seenIds.has(identity))) return false;
            identities.forEach((identity) => seenIds.add(identity));
            return true;
        }
        if (seenObjects.has(asset)) return false;
        seenObjects.add(asset);
        return true;
    });
};

export const hasPavMilestoneAssets = (ticket, milestone) => {
    if (getPavMilestoneAssets(ticket, milestone).length > 0) return true;
    if (Number(milestone?.asset_count || milestone?.assets_count || 0) > 0) return true;
    return false;
};

export const canRaisePavMilestoneSignOff = ({ ticket, milestone, hasSavedMilestoneAsset = false }) => {
    if (!isPavMilestone(milestone)) return false;

    const hasReachedSite = [
        MilestoneSubStatus.FIELD_ACCEPTED,
        MilestoneSubStatus.ENGINEER_AT_SITE,
        MilestoneSubStatus.ENGINEER_LEFT_SITE,
        MilestoneSubStatus.SITE_COMPLETED,
        MilestoneSubStatus.SIGN_OFF_REQUESTED,
        MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED,
        MilestoneSubStatus.CUSTOMER_SIGN_OFF_REJECTED,
        MilestoneSubStatus.SIGN_OFF_REJECTED,
        MilestoneSubStatus.SIGN_OFF_ACCEPTED,
        MilestoneSubStatus.HARD_CLOSED,
    ].includes(milestone?.milestone_status);
    const isSignOffRaised = ![MilestoneSubStatus.SIGN_OFF_REJECTED, MilestoneSubStatus.CUSTOMER_SIGN_OFF_REJECTED].includes(milestone?.milestone_status)
        && [MilestoneSubStatus.SIGN_OFF_REQUESTED, MilestoneSubStatus.CUSTOMER_SIGN_OFF_ACCEPTED, MilestoneSubStatus.SIGN_OFF_ACCEPTED, MilestoneSubStatus.HARD_CLOSED].includes(milestone?.milestone_status);

    return hasReachedSite
        && (hasSavedMilestoneAsset || hasPavMilestoneAssets(ticket, milestone))
        && !isSignOffRaised
        && milestone?.milestone_status !== MilestoneSubStatus.HARD_CLOSED
        && ![TicketStatus.CLOSED, TicketStatus.ON_HOLD].includes(ticket?.status);
};

const PAV_OCR_ORG_ID = 'SCOGO';

export const extractPavAsset = async (fileUrl) => {
    const token = getNewAuthToken();
    if (!token) throw new Error('An authorization token is required for OCR.');
    if (!config.api.pythonApiUrl) throw new Error('PAV API URL is not configured for this environment.');

    const response = await fetch(`${config.api.pythonApiUrl}/assets/extract`, {
        method: 'POST',
        headers: {
            accept: 'application/json',
            'x-org-id': PAV_OCR_ORG_ID,
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ file_url: fileUrl }),
    });
    const responseBody = await response.json();
    if (!response.ok) throw new Error(responseBody?.message || responseBody?.messages?.[0] || 'OCR failed.');
    return responseBody?.data || {};
};
